/* eslint-disable @typescript-eslint/no-explicit-any */
// SPEC: https://vingle.atlassian.net/wiki/spaces/BT/pages/835649548/Realtime
import { GROUP_ID } from "@/constant/group";
import { LOCAL_STORAGE_KEYS } from "@/constant/keys";
import { io, Socket } from "socket.io-client";
import React from "react";

export type IChannelSocket = Socket<{
  "new-message";
  "updated-message";
}> | null;

export type ConnectionFuncSignature = (
  channelId: Moim.Id,
  overrideGroupId?: Moim.Id,
  payload?: Record<string, any>,
) => void;

interface IContext {
  connectionPayload: Record<
    "postId" | "sort" | "before" | "after" | string,
    any
  >;
  isDisconnected: boolean;
  isConnecting: boolean;

  socketInstance: IChannelSocket;

  connectTo: ConnectionFuncSignature;
  disconnect(): void;
}

const VoidFunction = () => {};

const ChannelEventContext = React.createContext<IContext>({
  connectionPayload: {},
  isDisconnected: true,
  isConnecting: false,
  socketInstance: null,

  connectTo: VoidFunction,
  disconnect: VoidFunction,
});

export const ChannelEventProvider = ({ children }: React.PropsWithChildren) => {
  const refSocketInstance = React.useRef<IChannelSocket>(null);
  const refConnectionPayload = React.useRef<Record<string, any>>({});
  const [isDisconnected, setDisconnected] = React.useState(true);
  const [isConnecting, setConnectingStatus] = React.useState(false);

  const currentGroupId = GROUP_ID;
  const accessToken = localStorage.getItem(LOCAL_STORAGE_KEYS.AUTH_TOKEN);

  const socketDisConnect = React.useCallback(() => {
    console.log(">>>>> [ChannelEvent] socket Close()");
    setDisconnected(true);
    refSocketInstance.current?.close();
  }, []);

  const recvNewMessage = React.useCallback(data => {
    console.log(">>>>> recvNewMessage", data);
  }, []);
  const recvUpdatedMessage = React.useCallback(data => {
    console.log(">>>>> recvUpdatedMessage", data);
  }, []);

  const handleConnectTo = React.useCallback<ConnectionFuncSignature>(
    (channelId, overrideGroupId, payload) => {
      const connectionGroupId = overrideGroupId ?? currentGroupId;
      setConnectingStatus(true);
      setDisconnected(true);

      const matchEventType = (() => {
        switch (channelId.charAt(0)) {
          case "Q":
            return "posts";
          case "C":
            return "conversations";
        }
      })();

      if (
        (refConnectionPayload.current.channelId &&
          refConnectionPayload.current.channelId === channelId) ||
        !connectionGroupId
      ) {
        console.log(
          ">>>>> [ChannelEvent] Already same channel connected or invalid token",
        );
        return;
      }

      try {
        if (refSocketInstance.current && refSocketInstance.current.connected) {
          console.log(">>>>> [ChannelEvent] already connected, close it");
          socketDisConnect();
        }
        const sockURL = new URL(
          `https://event-v3.vingle.network/event-v3/${matchEventType}`,
        );
        if (accessToken) {
          sockURL.searchParams.set("accessToken", accessToken);
        }
        sockURL.searchParams.set("groupId", connectionGroupId);
        sockURL.searchParams.set("channelId", channelId);
        if (payload) {
          Object.entries(payload).forEach(([key, value]) => {
            if (value) {
              sockURL.searchParams.set(key, value);
            }
          });
        }

        console.log(">>>>> [ChannelEvent] try to connect", sockURL.toString());
        const sockInst = io(sockURL.toString(), {
          retries: 5,
          transports: ["websocket"],
        });

        sockInst.on("connect", () => {
          console.log(">>>>> [ChannelEvent] socket Opened");
          setConnectingStatus(false);
          setDisconnected(false);
        });

        sockInst.on("disconnect", reason => {
          if (!sockInst.active) {
            console.log(">>>>> [ChannelEvent] socket disconnected", reason);
            setDisconnected(true);
            setConnectingStatus(false);
            refConnectionPayload.current = {};
            refSocketInstance.current = null;
          }
        });

        sockInst.on("new-message", recvNewMessage);
        sockInst.on("updated-message", recvUpdatedMessage);

        sockInst.on("connect_error", () => {
          console.log(
            ">>>>>> [ChannelEvent] Socket error accrued during connecting",
          );
        });
        sockInst.on("reconnect_attempt", () => {
          console.log(">>>>>> [ChannelEvent] Socket trying to reconnect");
        });
        sockInst.on("reconnect", () => {
          console.log(">>>>>> [ChannelEvent] Socket successfully reconnected");
        });
        sockInst.on("reconnect_error", () => {
          console.log(">>>>>> [ChannelEvent] Socket failed to reconnect");
        });

        refSocketInstance.current = sockInst;
      } catch (error) {
        console.log(">>>>>>>>> [channelEvent] Catch error", error);
      }
    },
    [
      accessToken,
      currentGroupId,
      recvNewMessage,
      recvUpdatedMessage,
      socketDisConnect,
    ],
  );

  return (
    <ChannelEventContext.Provider
      value={{
        connectionPayload: refConnectionPayload.current,
        isDisconnected,
        isConnecting,
        socketInstance: refSocketInstance.current,
        connectTo: handleConnectTo,
        disconnect: socketDisConnect,
      }}
    >
      {children}
    </ChannelEventContext.Provider>
  );
};

export const useChannelEventContext = () => {
  return React.useContext(ChannelEventContext);
};
